import type { LibrarySketch } from './types';

export const generatorSketches: LibrarySketch[] = [
  {
    id: 'generators-flow-field-perlin',
    name: 'Perlin Flow Field',
    description: 'Thousands of particles trace paths through a Perlin noise flow field, creating organic silk-like patterns.',
    category: 'generators',
    tags: ['flow-field', 'perlin', 'particles', 'organic', 'generative'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'particleCount', type: 'int', min: 100, max: 5000, default: 2000, label: 'Particles' },
      { name: 'noiseScale', type: 'float', min: 0.001, max: 0.02, default: 0.005, label: 'Noise Scale' },
      { name: 'speed', type: 'float', min: 0.5, max: 5.0, default: 2.0, label: 'Speed' },
      { name: 'fadeAlpha', type: 'int', min: 1, max: 30, default: 5, label: 'Trail Length' },
    ],
    code: `// @param int particleCount 100 5000 2000
// @param float noiseScale 0.001 0.02 0.005
// @param float speed 0.5 5.0 2.0
// @param int fadeAlpha 1 30 5

let particles = [];
function setup() {
  createCanvas(genie.width, genie.height);
  background(0);
  const count = genie.params.particleCount ?? 2000;
  for (let i = 0; i < count; i++) {
    particles.push({ x: random(width), y: random(height) });
  }
}
function draw() {
  const ns = genie.params.noiseScale ?? 0.005;
  const spd = genie.params.speed ?? 2.0;
  const fade = genie.params.fadeAlpha ?? 5;
  fill(0, fade);
  noStroke();
  rect(0, 0, width, height);
  stroke(255, 50);
  strokeWeight(0.5);
  for (const p of particles) {
    const angle = noise(p.x * ns, p.y * ns, frameCount * 0.005) * TWO_PI * 2;
    p.x += cos(angle) * spd;
    p.y += sin(angle) * spd;
    point(p.x, p.y);
    if (p.x < 0 || p.x > width || p.y < 0 || p.y > height) {
      p.x = random(width);
      p.y = random(height);
    }
  }
}`,
  },

  {
    id: 'generators-fractal-mandelbrot',
    name: 'Mandelbrot Explorer',
    description: 'Interactive Mandelbrot set rendered on GPU with smooth coloring and animated zoom.',
    category: 'fractals',
    tags: ['fractal', 'mandelbrot', 'math', 'zoom', 'gpu'],
    runtimeMode: 'glsl',
    inputTypes: [],
    params: [
      { name: 'maxIter', type: 'int', min: 50, max: 500, default: 200, label: 'Iterations' },
      { name: 'zoomSpeed', type: 'float', min: 0.0, max: 1.0, default: 0.2, label: 'Zoom Speed' },
      { name: 'colorSpeed', type: 'float', min: 0.1, max: 3.0, default: 0.5, label: 'Color Speed' },
    ],
    code: `// @param int maxIter 50 500 200
// @param float zoomSpeed 0.0 1.0 0.2
// @param float colorSpeed 0.1 3.0 0.5

precision highp float;
uniform vec2 u_resolution;
uniform float u_time;
uniform int maxIter;
uniform float zoomSpeed;
uniform float colorSpeed;

vec3 palette(float t) {
  return 0.5 + 0.5 * cos(6.28318 * (t * colorSpeed + vec3(0.0, 0.33, 0.67)));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * u_resolution) / min(u_resolution.x, u_resolution.y);
  float zoom = exp(-u_time * zoomSpeed);
  vec2 center = vec2(-0.745, 0.186);
  vec2 c = center + uv * 2.0 * zoom;
  vec2 z = vec2(0.0);
  int iter = 0;
  for (int i = 0; i < 500; i++) {
    if (i >= maxIter) break;
    z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    if (dot(z, z) > 4.0) break;
    iter = i;
  }
  float t = float(iter) / float(maxIter);
  vec3 col = (iter >= maxIter - 1) ? vec3(0.0) : palette(t + u_time * 0.1);
  gl_FragColor = vec4(col, 1.0);
}`,
  },

  {
    id: 'generators-voronoi-cells',
    name: 'Animated Voronoi',
    description: 'Animated Voronoi diagram with drifting seed points and colorful cell boundaries.',
    category: 'generators',
    tags: ['voronoi', 'cells', 'diagram', 'generative', 'colorful'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'cellCount', type: 'int', min: 5, max: 80, default: 30, label: 'Cell Count' },
      { name: 'speed', type: 'float', min: 0.2, max: 3.0, default: 1.0, label: 'Speed' },
      { name: 'showEdges', type: 'bool', default: true, label: 'Show Edges' },
    ],
    code: `// @param int cellCount 5 80 30
// @param float speed 0.2 3.0 1.0
// @param bool showEdges true

let seeds = [];
function setup() {
  createCanvas(genie.width, genie.height);
  pixelDensity(1);
  const count = genie.params.cellCount ?? 30;
  for (let i = 0; i < count; i++) {
    seeds.push({
      x: random(width), y: random(height),
      vx: random(-1, 1), vy: random(-1, 1),
      col: color(random(100, 255), random(100, 255), random(100, 255))
    });
  }
}
function draw() {
  const spd = genie.params.speed ?? 1.0;
  loadPixels();
  for (let x = 0; x < width; x += 2) {
    for (let y = 0; y < height; y += 2) {
      let minD = Infinity, minD2 = Infinity, closest = 0;
      for (let i = 0; i < seeds.length; i++) {
        const d = dist(x, y, seeds[i].x, seeds[i].y);
        if (d < minD) { minD2 = minD; minD = d; closest = i; }
        else if (d < minD2) { minD2 = d; }
      }
      const c = seeds[closest].col;
      const edge = (minD2 - minD < 3 && (genie.params.showEdges ?? true)) ? 0 : 1;
      const idx = (y * width + x) * 4;
      pixels[idx] = red(c) * edge;
      pixels[idx + 1] = green(c) * edge;
      pixels[idx + 2] = blue(c) * edge;
      pixels[idx + 3] = 255;
      for (let dx = 0; dx < 2; dx++) for (let dy = 0; dy < 2; dy++) {
        const i2 = ((y + dy) * width + (x + dx)) * 4;
        pixels[i2] = pixels[idx]; pixels[i2+1] = pixels[idx+1];
        pixels[i2+2] = pixels[idx+2]; pixels[i2+3] = 255;
      }
    }
  }
  updatePixels();
  for (const s of seeds) {
    s.x += s.vx * spd; s.y += s.vy * spd;
    if (s.x < 0 || s.x > width) s.vx *= -1;
    if (s.y < 0 || s.y > height) s.vy *= -1;
  }
}`,
  },

  {
    id: 'generators-spirograph',
    name: 'Parametric Spirograph',
    description: 'A classic spirograph drawn with parametric equations, producing intricate looping patterns.',
    category: 'generators',
    tags: ['spirograph', 'parametric', 'math', 'pattern', 'curves'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'outerRadius', type: 'float', min: 50, max: 300, default: 200, label: 'Outer Radius' },
      { name: 'innerRadius', type: 'float', min: 10, max: 200, default: 80, label: 'Inner Radius' },
      { name: 'penOffset', type: 'float', min: 5, max: 150, default: 60, label: 'Pen Offset' },
      { name: 'speed', type: 'float', min: 0.5, max: 5.0, default: 2.0, label: 'Speed' },
    ],
    code: `// @param float outerRadius 50 300 200
// @param float innerRadius 10 200 80
// @param float penOffset 5 150 60
// @param float speed 0.5 5.0 2.0

let angle = 0;
function setup() {
  createCanvas(genie.width, genie.height);
  background(0);
  strokeWeight(0.8);
  noFill();
}
function draw() {
  translate(width / 2, height / 2);
  const R = genie.params.outerRadius ?? 200;
  const r = genie.params.innerRadius ?? 80;
  const d = genie.params.penOffset ?? 60;
  const spd = genie.params.speed ?? 2.0;
  const steps = 20;
  for (let i = 0; i < steps; i++) {
    const t = angle;
    const x1 = (R - r) * cos(t) + d * cos(((R - r) / r) * t);
    const y1 = (R - r) * sin(t) - d * sin(((R - r) / r) * t);
    angle += 0.02 * spd;
    const t2 = angle;
    const x2 = (R - r) * cos(t2) + d * cos(((R - r) / r) * t2);
    const y2 = (R - r) * sin(t2) - d * sin(((R - r) / r) * t2);
    const hue = (t * 10) % 360;
    colorMode(HSB, 360, 100, 100, 100);
    stroke(hue, 80, 100, 30);
    line(x1, y1, x2, y2);
  }
}`,
  },

  {
    id: 'generators-noise-landscape',
    name: '3D Noise Terrain',
    description: 'A flyover of a 3D terrain surface generated from layered Perlin noise, rendered with a wireframe grid.',
    category: 'generators',
    tags: ['terrain', 'landscape', '3d', 'noise', 'wireframe', 'flyover'],
    runtimeMode: 'threejs',
    inputTypes: [],
    params: [
      { name: 'noiseScale', type: 'float', min: 0.01, max: 0.1, default: 0.04, label: 'Noise Scale' },
      { name: 'heightScale', type: 'float', min: 10, max: 100, default: 40, label: 'Height' },
      { name: 'flySpeed', type: 'float', min: 0.1, max: 2.0, default: 0.5, label: 'Fly Speed' },
      { name: 'wireColor', type: 'color', default: '#00ffaa', label: 'Wire Color' },
    ],
    code: `// @param float noiseScale 0.01 0.1 0.04
// @param float heightScale 10 100 40
// @param float flySpeed 0.1 2.0 0.5
// @param color wireColor #00ffaa

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(60, genie.width / genie.height, 0.1, 1000);
camera.position.set(0, 60, 80);
camera.lookAt(0, 0, 0);
const renderer = new THREE.WebGLRenderer({ canvas: genie.canvas, antialias: true });
renderer.setSize(genie.width, genie.height);
const gridW = 80, gridH = 80;
const geo = new THREE.PlaneGeometry(200, 200, gridW, gridH);
geo.rotateX(-Math.PI / 2);
const wireCol = genie.params.wireColor ?? '#00ffaa';
const mat = new THREE.MeshBasicMaterial({ color: wireCol, wireframe: true });
const mesh = new THREE.Mesh(geo, mat);
scene.add(mesh);
scene.fog = new THREE.Fog(0x000000, 50, 200);
// Simple noise using sin combination
function noise2d(x, y) {
  return (Math.sin(x * 1.2 + y * 0.7) + Math.sin(x * 0.8 - y * 1.5)
    + Math.sin(x * 2.1 + y * 1.3) * 0.5) / 2.5;
}
let offset = 0;
function animate() {
  requestAnimationFrame(animate);
  const ns = genie.params.noiseScale ?? 0.04;
  const hs = genie.params.heightScale ?? 40;
  const spd = genie.params.flySpeed ?? 0.5;
  offset += spd * 0.02;
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const y = noise2d(x * ns, (z + offset * 50) * ns) * hs;
    pos.setY(i, y);
  }
  pos.needsUpdate = true;
  renderer.render(scene, camera);
}
animate();`,
  },

  {
    id: 'generators-cellular-automata',
    name: 'Game of Life',
    description: "Conway's Game of Life cellular automaton with randomized initial state and adjustable simulation speed.",
    category: 'generators',
    tags: ['cellular', 'automata', 'life', 'simulation', 'conway'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'cellSize', type: 'int', min: 2, max: 20, default: 6, label: 'Cell Size' },
      { name: 'fillRate', type: 'float', min: 0.1, max: 0.9, default: 0.3, label: 'Initial Fill' },
      { name: 'simSpeed', type: 'int', min: 1, max: 30, default: 10, label: 'Speed (fps)' },
    ],
    code: `// @param int cellSize 2 20 6
// @param float fillRate 0.1 0.9 0.3
// @param int simSpeed 1 30 10

let grid, cols, rows, cs;
function setup() {
  createCanvas(genie.width, genie.height);
  cs = genie.params.cellSize ?? 6;
  cols = floor(width / cs);
  rows = floor(height / cs);
  grid = Array.from({ length: cols }, () =>
    Array.from({ length: rows }, () => random() < (genie.params.fillRate ?? 0.3) ? 1 : 0)
  );
}
function draw() {
  frameRate(genie.params.simSpeed ?? 10);
  background(0);
  noStroke();
  fill(0, 255, 100);
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      if (grid[x][y]) rect(x * cs, y * cs, cs - 1, cs - 1);
    }
  }
  const next = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      let n = 0;
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue;
        const nx = (x + dx + cols) % cols;
        const ny = (y + dy + rows) % rows;
        n += grid[nx][ny];
      }
      if (grid[x][y]) next[x][y] = (n === 2 || n === 3) ? 1 : 0;
      else next[x][y] = (n === 3) ? 1 : 0;
    }
  }
  grid = next;
}`,
  },
];
